/*
 * card-gallery-lazy — controller for the lightweight PLP card carousel (snippets/card-gallery-2.liquid).
 *
 * Why lightweight (not the theme slideshow): the real <slideshow-component> renders a heavy
 * per-card DOM (scroll-snap scroller + all slideshow-slides forced visible on mobile + a Scroller
 * and ResizeObserver each). ×36 cards, that paints too slowly on iOS and cards flicker/blank in on
 * scroll. This carousel is plain scroll-snap divs, so it paints fast. This element adds back the
 * behaviour the slideshow would have given, cheaply:
 *
 *   - Primary image loaded ~600px before the card enters the viewport (Safari's native lazy is too
 *     late and blanks below-fold cards on every page, incl. page 2+).
 *   - Non-primary slides are <img data-src> (never decoded at rest => ~1 decoded image/card, the
 *     iOS memory fix); promoted to src when revealed, next slide preloaded.
 *   - Colour / variant switching for the Shop LC custom swatches
 *     (`.shoplc-product-card__variant-swatch[data-card-swatch][data-variant-id]`). These are
 *     in-product colour variants, each with its OWN image set (~4-5 images) — only the featured
 *     image is present in the initial DOM, so we cannot just show/hide. We reuse the SAME server
 *     endpoint the live theme uses (`section-rendering-product-card-media`, which renders a variant's
 *     colour-grouped media as `[data-media]` items), then rebuild this track from it. Media is
 *     prefetched on interaction-intent (hover / first touch) and cached, so switching feels instant.
 *     Rebuilt slides are <img data-src> too (only the first decodes) — the iOS fix is preserved.
 *   - Dots (right, above ATC) + prev/next arrows, kept in sync with the scroll-snap track.
 *   - Exposes a small slideshow-compatible shim (ref="slideshow") so product-card.js's own
 *     numeric select() calls (hover second image) also drive this carousel harmlessly.
 */
const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;
const VARIANT_MEDIA_SECTION = 'section-rendering-product-card-media';
// Standard Shopify CDN widths (subset the section already emits) — snapping to these keeps CDN caching efficient.
const IMG_WIDTHS = [240, 352, 480, 640, 832, 1200, 1600];

class CardGalleryLazy extends HTMLElement {
  connectedCallback() {
    // Run setup AFTER the current DOM mutation settles. On filter/sort the collection section is
    // morphed, which MOVES these elements (re-firing connectedCallback ~once per card) and strips the
    // JS-injected dots/arrows (they aren't in the server HTML). Deferring one frame lets the morph
    // finish first, then we rebuild idempotently — this is what makes images + controls come back
    // after a filter/sort. rAF also collapses the reconnect burst into a single setup per element.
    cancelAnimationFrame(this.#initRaf);
    this.#initRaf = requestAnimationFrame(() => this.#setup());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#initRaf);
    this.#io?.disconnect();
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('pointerenter', this.#onHoverEnter);
    this.removeEventListener('pointerleave', this.#onHoverLeave);
    this.#card?.removeEventListener('change', this.#onSwatchChange);
    this.#card?.removeEventListener('pointerover', this.#onSwatchOver);
    this.#card?.removeEventListener('pointerleave', this.#onCardLeave);
  }

  // Idempotent (re)initialization — safe to run on first connect AND after every morph.
  #setup() {
    if (!this.isConnected) return;
    const track = this.querySelector('.card-carousel-2__track');
    if (!track) return;
    this.#track = track;

    // Primary image: (re)observe so it loads ~600px before entering the viewport. After a morph the
    // observer fires immediately if the card is already on screen, so images repaint right away.
    this.#io?.disconnect();
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#loadPrimary();
          this.#io.disconnect();
          break;
        }
      },
      { rootMargin: '600px 0px' }
    );
    this.#io.observe(this);

    // Controls: rebuild only if the morph stripped them (server HTML never contains them).
    this.#current = 0;
    if (this.#navSlides.length > 1) {
      if (!this.querySelector('.card-carousel-2__dots')) this.#renderDots();
      if (!this.querySelector('.card-carousel-2__arrows')) this.#renderArrows();
      track.removeEventListener('scroll', this.#onScroll);
      track.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    // Hover / first-touch (remove-then-add so re-setup never double-binds).
    this.removeEventListener('pointerenter', this.#onHoverEnter);
    this.removeEventListener('pointerleave', this.#onHoverLeave);
    if (isDesktop()) {
      this.addEventListener('pointerenter', this.#onHoverEnter);
      this.addEventListener('pointerleave', this.#onHoverLeave);
    } else {
      this.addEventListener('pointerdown', this.#onFirstTouch, { once: true, passive: true });
      this.addEventListener('touchstart', this.#onFirstTouch, { once: true, passive: true });
    }

    // Colour / variant switching off the Shop LC swatches (idempotent; re-resolve card after a morph).
    this.#card?.removeEventListener('change', this.#onSwatchChange);
    this.#card?.removeEventListener('pointerover', this.#onSwatchOver);
    this.#card?.removeEventListener('pointerleave', this.#onCardLeave);
    this.#card = this.closest('.shoplc-product-card');
    if (this.#card) {
      this.#productUrl = this.#card.getAttribute('data-product-url') || null;
      this.#committedSwatch = this.#card.querySelector(
        '[data-card-swatch].shoplc-product-card__variant-swatch--selected'
      );
      // Snapshot the current (default) track so a hover preview can revert cleanly.
      this.#initialTrackHTML = track.innerHTML;
      this.#initialTitleHTML = this.#card.querySelector('a[ref="productTitleLink"] .text-block p')?.innerHTML ?? null;
      this.#initialPriceHTML = this.#card.querySelector('product-price [ref="priceContainer"]')?.outerHTML ?? null;
      this.#committedVariantId = this.#committedSwatch?.getAttribute('data-variant-id') || null;
      this.#card.addEventListener('change', this.#onSwatchChange);
      if (isDesktop()) {
        this.#card.addEventListener('pointerover', this.#onSwatchOver);
        this.#card.addEventListener('pointerleave', this.#onCardLeave);
      }

      // The server card contains product.media, which can mix images from several colours.
      // Immediately normalize the complete carousel to the selected variant's media payload.
      if (this.#committedVariantId) {
        this.#applyVariant(this.#committedVariantId, true);
      }
    }
  }

  #track;
  #card;
  #io;
  #dots = [];
  #prevBtn = null;
  #nextBtn = null;
  #current = 0;
  #raf = 0;
  #initRaf = 0;
  #productUrl = null;
  #committedVariantId = null; // last CLICKED swatch variant (revert target after a hover preview)
  #committedSwatch = null; // the swatch element to re-mark --selected on revert
  #pending = null; // most recent requested variant, to drop stale async renders
  #prefetched = false;
  #initialTrackHTML = null;
  #initialTitleHTML = null;
  #initialPriceHTML = null;
  #mediaCache = new Map(); // variantId -> { media, title, priceHtml }
  #inflight = new Map(); // variantId -> Promise (de-dupe concurrent fetches)
  #warmed = new Set(); // image srcs already preloaded

  get #allSlides() {
    return Array.from(this.querySelectorAll('.card-carousel-2__slide'));
  }

  get #navSlides() {
    return this.#allSlides.filter((slide) => !slide.hasAttribute('hidden'));
  }

  /* ---------- slideshow-compatible shim (product-card.js may also call these) ---------- */
  get isNested() {
    return false;
  }
  get slides() {
    return this.#allSlides;
  }
  get refs() {
    return { slides: this.#allSlides };
  }
  get current() {
    return this.#current;
  }
  get initialSlide() {
    return this.#allSlides[0] || null;
  }
  select(input) {
    let target = null;
    if (input && typeof input === 'object' && 'id' in input) {
      target = this.#allSlides.find((s) => s.getAttribute('slide-id') == input.id) || null;
    } else if (typeof input === 'number') {
      target = this.#navSlides[input] || null;
    }
    this.#goToSlide(target);
  }
  previous() {
    this.#goToSlide(this.#navSlides[Math.max(0, this.#current - 1)]);
  }

  /* ---------- Shop LC custom swatch handling (fetch variant media -> rebuild track) ---------- */
  #swatchFrom(event) {
    const swatch = event.target?.closest?.('[data-card-swatch][data-variant-id]');
    return swatch && this.#card?.contains(swatch) ? swatch : null;
  }

  // Warm the cache for every swatch the moment the user shows intent to interact with this card,
  // so the actual select/hover renders from cache instantly (no visible network wait).
  #prefetchSwatches() {
    if (this.#prefetched || !this.#card) return;
    this.#prefetched = true;
    this.#card.querySelectorAll('[data-card-swatch][data-variant-id]').forEach((sw) => {
      this.#fetchVariantMedia(sw.getAttribute('data-variant-id'));
    });
  }

  #fetchVariantMedia(variantId) {
    if (!variantId || !this.#productUrl) return Promise.resolve({ media: [], title: '', priceHtml: '' });
    const cached = this.#mediaCache.get(variantId);
    if (cached) return Promise.resolve(cached);
    const pending = this.#inflight.get(variantId);
    if (pending) return pending;

    let url;
    try {
      url = new URL(this.#productUrl, window.location.origin);
    } catch {
      return Promise.resolve({ media: [], title: '', priceHtml: '' });
    }
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', VARIANT_MEDIA_SECTION);
    const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewId) url.searchParams.set('preview_theme_id', previewId);

    const request = fetch(url.toString(), { credentials: 'same-origin' })
      .then((res) => res.text())
      .then((text) => {
        let payload = { media: [], title: '', priceHtml: '' };
        try {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const root = doc.querySelector('[data-product-card-variant-media]');
          if (root) {
            const media = Array.from(root.querySelectorAll('[data-media]'))
              .map((el) => ({
                id: el.getAttribute('data-id') || '',
                src: el.getAttribute('data-src') || '',
                srcset: el.getAttribute('data-srcset') || '',
                alt: el.getAttribute('data-alt') || '',
              }))
              .filter((m) => m.src);
            const priceTemplate = root.querySelector('template[data-variant-price]');
            payload = {
              media,
              title: root.getAttribute('data-variant-title') || '',
              priceHtml: priceTemplate instanceof HTMLTemplateElement ? priceTemplate.innerHTML.trim() : '',
            };
          }
        } catch {
          /* ignore parse errors, fall through to [] */
        }
        this.#inflight.delete(variantId);
        if (payload.media.length) {
          this.#mediaCache.set(variantId, payload);
          this.#warmImage(this.#displaySrc(payload.media[0].src)); // download the featured image ahead of the switch
        }
        return payload;
      })
      .catch(() => {
        this.#inflight.delete(variantId);
        return { media: [], title: '', priceHtml: '' };
      });

    this.#inflight.set(variantId, request);
    return request;
  }

  #applyVariant(variantId, commit) {
    if (!variantId) return;
    this.#pending = variantId;
    this.#fetchVariantMedia(variantId).then((payload) => {
      if (this.#pending !== variantId) return; // a newer hover/select superseded this one
      if (payload.media.length) {
        this.#renderMedia(payload.media);
        // Warm this variant's swipe images too, so scrolling within the variant is instant.
        payload.media.forEach((m) => this.#warmImage(this.#displaySrc(m.src)));
      }
      this.#applyCardDetails(payload);
      if (commit) this.#committedVariantId = variantId;
    });
  }

  #revertVariant() {
    if (this.#committedSwatch) {
      this.#markSelected(this.#committedSwatch);
      this.#applyVariantState(this.#committedSwatch);
    }
    this.#pending = this.#committedVariantId;
    const committed = this.#committedVariantId && this.#mediaCache.get(this.#committedVariantId);
    if (committed) {
      this.#renderMedia(committed.media);
      this.#applyCardDetails(committed);
    } else if (this.#initialTrackHTML != null) {
      this.#track.innerHTML = this.#initialTrackHTML;
      this.#resetControls();
    }
    if (!this.#committedVariantId) this.#restoreInitialDetails();
  }

  #applyCardDetails(payload) {
    if (!this.#card) return;
    const title = this.#card.querySelector('a[ref="productTitleLink"] .text-block p');
    if (title && payload.title) title.textContent = payload.title;
    if (payload.priceHtml) {
      const doc = new DOMParser().parseFromString(payload.priceHtml, 'text/html');
      const nextPrice = doc.querySelector('[ref="priceContainer"]');
      const currentPrice = this.#card.querySelector('product-price [ref="priceContainer"]');
      if (nextPrice && currentPrice) currentPrice.replaceWith(nextPrice);
    }
  }

  #applyVariantState(swatch) {
    if (!this.#card) return;
    const variantId = swatch.getAttribute('data-variant-id');
    const title = this.#card.querySelector('a[ref="productTitleLink"] .text-block p');
    if (title && swatch.dataset.variantTitle) title.textContent = swatch.dataset.variantTitle;
    this.#card.dataset.selectedVariantId = variantId || '';
    this.#card.querySelectorAll('a[href]').forEach((link) => {
      try {
        const url = new URL(link.href, window.location.origin);
        const productPath = new URL(this.#productUrl, window.location.origin).pathname;
        if (url.pathname === productPath && variantId) {
          url.searchParams.set('variant', variantId);
          link.href = url.toString();
        }
      } catch { /* ignore invalid URLs */ }
    });
    this.#card.querySelectorAll('.shoplc-product-card__quick-add-cta, .shoplc-product-card__variant-overflow')
      .forEach((button) => button.setAttribute('data-variant-id', variantId || ''));
  }

  #restoreInitialDetails() {
    if (!this.#card) return;
    const title = this.#card.querySelector('a[ref="productTitleLink"] .text-block p');
    if (title && this.#initialTitleHTML != null) title.innerHTML = this.#initialTitleHTML;
    const price = this.#card.querySelector('product-price [ref="priceContainer"]');
    if (price && this.#initialPriceHTML) price.outerHTML = this.#initialPriceHTML;
  }

  #warmImage(src) {
    if (!src || this.#warmed.has(src)) return;
    this.#warmed.add(src);
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  }

  // The width this card's slot actually needs (slot px × DPR, capped at 2× for perf), snapped to a
  // standard Shopify width. Requesting exactly this delivers a right-sized image per device AND lets
  // us prewarm the identical URL the browser will paint (a srcset would re-introduce a warm mismatch).
  #targetWidth() {
    const css = this.#track?.clientWidth || 300;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const need = css * dpr;
    return IMG_WIDTHS.find((w) => w >= need) || IMG_WIDTHS[IMG_WIDTHS.length - 1];
  }

  #displaySrc(src) {
    // Reject the STRING forms too, not just falsy values. A media entry with no src
    // returns undefined from here; assigning that to img.dataset.src stores the literal
    // string "undefined", which then passes a plain `!src` check on the next read and
    // ends up as img.src - a request for /undefined, which 404s. Returning '' lets the
    // two call sites below skip the assignment entirely.
    if (!src || src === 'undefined' || src === 'null') return '';
    const w = this.#targetWidth();
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, `$1${w}`);
    return `${src}${src.includes('?') ? '&' : '?'}width=${w}`;
  }

  #markSelected(swatch) {
    this.#card
      ?.querySelectorAll('.shoplc-product-card__variant-swatch--selected')
      .forEach((el) => el.classList.remove('shoplc-product-card__variant-swatch--selected'));
    swatch.classList.add('shoplc-product-card__variant-swatch--selected');
  }

  // Radio change = the variant is chosen (tap on mobile, click on desktop): commit + show it.
  #onSwatchChange = (event) => {
    const swatch = this.#swatchFrom(event);
    if (!swatch) return;
    this.#committedSwatch = swatch;
    this.#committedVariantId = swatch.getAttribute('data-variant-id');
    this.#markSelected(swatch);
    this.#applyVariantState(swatch);
    this.#applyVariant(swatch.getAttribute('data-variant-id'), true);
  };

  // Desktop swatch hover = treat the hovered variant as the active one: mark it selected and show
  // its content. It sticks (carousel stays on it, swipeable) until another swatch or the card exit.
  #onSwatchOver = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    const swatch = this.#swatchFrom(event);
    if (!swatch) return;
    this.#markSelected(swatch);
    this.#applyVariantState(swatch);
    this.#applyVariant(swatch.getAttribute('data-variant-id'), false);
  };

  // Pointer left the whole card: revert to the last committed (clicked / on-load) variant.
  #onCardLeave = () => this.#revertVariant();

  // Rebuild the track from a fetched variant's media. Only the first image decodes now (src);
  // the rest are data-src and promote on swipe — keeps the iOS at-rest decode count at ~1.
  #renderMedia(mediaList) {
    if (!this.#track || !mediaList?.length) return;
    const frag = document.createDocumentFragment();
    mediaList.forEach((media, index) => {
      const slide = document.createElement('div');
      slide.className = 'card-carousel-2__slide product-media-container';
      slide.setAttribute('slide-id', media.id);
      const img = document.createElement('img');
      img.className = 'card-carousel-2__img';
      img.alt = media.alt;
      img.decoding = 'async';
      // Right-size the image to this card's slot (see #displaySrc). Single src, no srcset, so the
      // URL the browser paints is EXACTLY the one we prewarm — a guaranteed cache hit.
      const src = this.#displaySrc(media.src);
      // Only assign when there is a real url. `img.src = ''` resolves against the document
      // and re-requests the PAGE, and an empty data-src still matches the img[data-src]
      // selector in #promote, so both branches need the guard.
      if (index === 0) {
        img.loading = 'eager';
        img.fetchPriority = 'high';
        if (src) img.src = src;
      } else {
        img.loading = 'lazy';
        if (src) img.dataset.src = src;
      }
      slide.appendChild(img);
      frag.appendChild(slide);
    });
    this.#track.replaceChildren(frag);
    this.#resetControls();
  }

  #resetControls() {
    this.#current = 0;
    this.#dots = [];
    this.querySelector('.card-carousel-2__dots')?.remove();
    this.querySelector('.card-carousel-2__arrows')?.remove();
    if (this.#navSlides.length > 1) {
      this.#renderDots();
      this.#renderArrows();
    }
    this.#track.scrollTo({ left: 0, behavior: 'auto' });
  }

  /* ---------- navigation ---------- */
  #goToSlide(slide) {
    if (!slide || !this.#track) return;
    slide.hidden = false;
    this.#promote(slide);

    this.#syncControls();
    const nav = this.#navSlides;
    const index = nav.indexOf(slide);
    if (index < 0) return;

    this.#promote(nav[index + 1]);
    this.#current = index;
    this.#track.scrollTo({ left: index * this.#track.clientWidth, behavior: 'auto' });
    this.#updateDots();
    this.#updateArrows();
  }

  #onHoverEnter = () => {
    this.#prefetchSwatches();
    if (this.#current === 0) this.select(1);
  };
  // Leaving the gallery image returns to the first image of the CURRENT set (variant revert is
  // handled by #onCardLeave, so a hovered variant survives moving between gallery and swatches).
  #onHoverLeave = () => {
    if (this.#navSlides.length > 1) this.select(0);
  };

  #onFirstTouch = () => {
    this.#prefetchSwatches();
    this.#promote(this.#navSlides[1]);
  };

  #onScroll = () => {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#syncControls();
      const width = this.#track.clientWidth || 1;
      const index = Math.round(this.#track.scrollLeft / width);
      if (index !== this.#current) {
        this.#current = index;
        this.#updateDots();
        this.#updateArrows();
      }
      const nav = this.#navSlides;
      this.#promote(nav[index]);
      this.#promote(nav[index + 1]);
    });
  };

  /* ---------- dots + arrows (custom classes, styled in snippets/card-gallery-2.liquid) ---------- */
  #renderDots() {
    this.querySelector('.card-carousel-2__dots')?.remove();
    const dots = document.createElement('div');
    dots.className = 'card-carousel-2__dots';
    dots.setAttribute('aria-hidden', 'true');
    const count = this.#navSlides.length;
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('span');
      dot.className = 'card-carousel-2__dot' + (i === this.#current ? ' is-active' : '');
      dots.appendChild(dot);
    }
    this.#dots = Array.from(dots.children);
    this.appendChild(dots);
  }

  #syncControls() {
    const count = this.#navSlides.length;
    if (this.#current > count - 1) this.#current = Math.max(0, count - 1);
    if (this.#dots.length !== count) this.#renderDots();
  }

  #renderArrows() {
    const wrap = document.createElement('div');
    wrap.className = 'card-carousel-2__arrows';
    wrap.setAttribute('aria-hidden', 'true');
    this.#prevBtn = this.#makeArrow('prev');
    this.#nextBtn = this.#makeArrow('next');
    wrap.appendChild(this.#prevBtn);
    wrap.appendChild(this.#nextBtn);
    this.appendChild(wrap);
  }

  #makeArrow(dir) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `card-carousel-2__arrow card-carousel-2__arrow--${dir}`;
    btn.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
    const path =
      dir === 'prev'
        ? 'M6.99 1.53 5.46 0 0 7.5 5.46 15 6.99 13.47 3.06 7.5z'
        : 'M0.01 1.53 1.54 0 7 7.5 1.54 15 0.01 13.47 3.94 7.5z';
    btn.innerHTML =
      '<svg viewBox="0 0 7 15" width="7" height="15" aria-hidden="true">' +
      '<path d="' + path + '" fill="currentColor"/></svg>';
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.select(this.#current + (dir === 'next' ? 1 : -1));
    });
    return btn;
  }

  #updateDots() {
    this.#dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.#current));
  }

  #updateArrows() {
    // Arrows stay visible; clicks clamp at the ends (avoids space-between layout shift).
  }

  /* ---------- image loading ---------- */
  #loadPrimary() {
    this.querySelectorAll('img[loading="lazy"]:not([data-src])').forEach((img) => {
      img.loading = 'eager';
      if (img.fetchPriority === 'low') img.fetchPriority = 'auto';
    });
  }

  #promote(slide) {
    if (!slide) return;
    const img = slide.querySelector('img[data-src]');
    if (!img) return;
    // Belt and braces: data-src could still hold the string "undefined" if it was set
    // before this guard existed (or by any other code path), and assigning that would
    // request /undefined.
    const promoted = img.dataset.src;
    if (promoted && promoted !== 'undefined' && promoted !== 'null') img.src = promoted;
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      img.sizes = '50vw';
      img.removeAttribute('data-srcset');
    }
    img.removeAttribute('data-src');
  }
}

if (!customElements.get('card-gallery-lazy')) {
  customElements.define('card-gallery-lazy', CardGalleryLazy);
}
